import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, Calendar, Clock, User, CreditCard, ArrowLeft, Home } from 'lucide-react';
import { API } from '../utils/api';


export function PaymentConfirmation() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const preferenceId = searchParams.get('preference_id');
  const appointmentId = searchParams.get('appointment_id');
  const paymentId = sessionId || preferenceId;
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(function() {
    if (!paymentId) {
      setStatus('error');
      return;
    }

    async function checkPaymentStatus() {
      try {
        const res = await axios.get(API + '/payments/status/' + paymentId);
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentInfo(res.data);
        } else if (pollCount < 10) {
          // Continue polling
          setTimeout(function() {
            setPollCount(function(c) { return c + 1; });
          }, 2000);
        } else {
          setStatus('pending');
          setPaymentInfo(res.data);
        }
      } catch (e) {
        console.error('Error checking payment:', e);
        if (pollCount < 5) {
          setTimeout(function() {
            setPollCount(function(c) { return c + 1; });
          }, 2000);
        } else {
          setStatus('error');
        }
      }
    }

    checkPaymentStatus();
  }, [paymentId, pollCount]);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          {status === 'loading' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-12 text-center">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-50 mb-2">Verificando pago...</h2>
                <p className="text-slate-400">Por favor espera mientras confirmamos tu pago</p>
              </CardContent>
            </Card>
          )}

          {status === 'success' && (
            <Card className="bg-slate-900 border-emerald-500/30">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-50 mb-2">¡Pago Exitoso!</h2>
                <p className="text-slate-400 mb-6">
                  Tu reserva ha sido confirmada. El profesional ha sido notificado y te contactará pronto.
                </p>
                
                {paymentInfo && (
                  <div className="bg-slate-800/50 rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm text-slate-400 mb-2">Detalles del pago:</p>
                    <div className="space-y-1">
                      <p className="text-slate-200">
                        <CreditCard className="w-4 h-4 inline mr-2 text-emerald-400" />
                        Monto: ${paymentInfo.amount?.toLocaleString('es-CL')} {paymentInfo.currency?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">
                    Recibirás un email de confirmación con los detalles de tu cita.
                  </p>
                  <Link to="/profesionales">
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver a Profesionales
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {status === 'pending' && (
            <Card className="bg-slate-900 border-yellow-500/30">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-50 mb-2">Pago Pendiente</h2>
                <p className="text-slate-400 mb-6">
                  Tu pago está siendo procesado. Si completaste el pago, recibirás una confirmación por email.
                </p>
                <Link to="/profesionales">
                  <Button variant="outline" className="border-slate-700 text-slate-300">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver a Profesionales
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {status === 'error' && (
            <Card className="bg-slate-900 border-red-500/30">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-50 mb-2">Error en el Pago</h2>
                <p className="text-slate-400 mb-6">
                  No pudimos verificar tu pago. Por favor contacta a soporte si el cobro fue realizado.
                </p>
                <div className="space-y-3">
                  <Link to="/profesionales">
                    <Button variant="outline" className="w-full border-slate-700 text-slate-300">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver a Profesionales
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function PaymentCancelled() {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-50 mb-2">Pago Cancelado</h2>
              <p className="text-slate-400 mb-6">
                Has cancelado el proceso de pago. Tu cita no ha sido confirmada.
              </p>
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  Puedes volver a intentar agendar una cita cuando lo desees.
                </p>
                <Link to="/profesionales">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Buscar Profesionales
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// New Mercado Pago payment result pages
export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');
  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');
  const externalReference = searchParams.get('external_reference');
  const entityType = searchParams.get('entity_type');
  const planType = searchParams.get('plan_type');
  const amount = searchParams.get('amount');

  var entityLabel = {'center': 'Centro', 'professional': 'Profesional', 'workshop': 'Taller'}[entityType] || '';
  var planLabel = {'destacado': 'Destacado', 'premium': 'Premium', 'elite': 'Elite'}[planType] || '';
  var dashboardPath = {'center': '/dashboard/centro', 'professional': '/dashboard/profesional', 'workshop': '/dashboard/taller'}[entityType];

  function formatCLP(val) {
    if (!val) return '';
    return '$' + Number(val).toLocaleString('es-CL');
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          <Card className="bg-slate-900 border-emerald-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-50 mb-3" data-testid="payment-success-title">Pago Exitoso</h2>
              <p className="text-slate-400 mb-6">
                Tu pago ha sido procesado correctamente. Recibirás una confirmación por email.
              </p>
              
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6 space-y-2">
                <p className="text-sm text-slate-500 mb-1">Estado del pago</p>
                <p className="text-emerald-400 font-semibold">Aprobado</p>
                {planLabel && (
                  <div className="pt-2 border-t border-slate-700">
                    <p className="text-sm text-slate-400">Plan: <span className="text-slate-200 font-medium">{planLabel} ({entityLabel})</span></p>
                  </div>
                )}
                {amount && (
                  <p className="text-sm text-slate-400">Monto: <span className="text-slate-200 font-medium">{formatCLP(amount)} CLP</span></p>
                )}
                {planType && (
                  <p className="text-sm text-slate-400">Vigencia: <span className="text-slate-200 font-medium">30 días</span></p>
                )}
              </div>
              
              <div className="space-y-3">
                {dashboardPath ? (
                  <Link to={dashboardPath}>
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="go-to-dashboard-btn">
                      Ir a mi Panel
                    </Button>
                  </Link>
                ) : (
                  <Link to="/">
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                      <Home className="w-4 h-4 mr-2" />
                      Volver al Inicio
                    </Button>
                  </Link>
                )}
                {appointmentId && (
                  <Link to="/profesionales">
                    <Button variant="outline" className="w-full border-slate-700 text-slate-300">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Ver Profesionales
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function PaymentFailed() {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');
  const reason = searchParams.get('reason');

  var reasonText = {
    'cancelled': 'Has cancelado el proceso de pago.',
    'rejected': 'El pago fue rechazado por el banco o la tarjeta.',
    'error': 'Ocurrió un error al procesar el pago.',
    'no_token': 'No se recibió confirmación del procesador de pagos.'
  }[reason] || 'El pago no pudo ser procesado. No se ha realizado ningún cargo a tu cuenta.';

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          <Card className="bg-slate-900 border-red-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-50 mb-3" data-testid="payment-failed-title">Pago No Completado</h2>
              <p className="text-slate-400 mb-6">{reasonText}</p>
              
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-slate-400 mb-2">Posibles soluciones:</p>
                <ul className="text-sm text-slate-500 list-disc list-inside space-y-1">
                  <li>Verifica que tu tarjeta tenga fondos suficientes</li>
                  <li>Intenta con otra tarjeta de crédito o débito</li>
                  <li>Contacta a tu banco si el problema persiste</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <Link to="/planes">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="retry-payment-btn">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Intentar de Nuevo
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full border-slate-700 text-slate-300">
                    <Home className="w-4 h-4 mr-2" />
                    Volver al Inicio
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function PaymentPending() {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          <Card className="bg-slate-900 border-yellow-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-50 mb-3">Pago Pendiente</h2>
              <p className="text-slate-400 mb-6">
                Tu pago está siendo procesado. Recibirás una notificación cuando se confirme.
              </p>
              
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-500 mb-1">Estado del pago</p>
                <p className="text-yellow-400 font-semibold">En proceso...</p>
              </div>
              
              <p className="text-sm text-slate-500 mb-6">
                Si pagaste con transferencia bancaria o efectivo, el pago puede demorar hasta 48 horas en acreditarse.
              </p>
              
              <div className="space-y-3">
                <Link to="/">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                    <Home className="w-4 h-4 mr-2" />
                    Volver al Inicio
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
